import { useEffect, useRef, useState } from "react";
import { API_BASE } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Music2,
  Play,
  Pause,
  X,
  Wand2,
  Sparkles,
  Loader2,
  ExternalLink,
  Copy,
  Check,
  Tag,
} from "lucide-react";

// Editor-side music control panel. Lives below the timeline.
// Props:
//   project    — current project document (must contain `music` block)
//   onPatchMusic(partial)  — persists `music` partial via PATCH /projects/:id
//   onPatchClipsMusic(suggestions) — persists per-clip music_track_id matches
export default function EditorMusicPanel({
  project,
  onPatchMusic,
  onPatchClipsMusic,
}) {
  const [tracks, setTracks] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(true);
  const [showAutoMatch, setShowAutoMatch] = useState(false);
  const [showSuno, setShowSuno] = useState(false);
  const audioRef = useRef(null);
  const [previewingId, setPreviewingId] = useState(null);

  const music = project.music || {};
  const projectId = project.id;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/music`, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTracks(data);
      } finally {
        if (!cancelled) setLoadingTracks(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const togglePreview = (trackId) => {
    const a = audioRef.current;
    if (!a) return;
    if (previewingId === trackId) {
      a.pause();
      setPreviewingId(null);
      return;
    }
    a.src = `${API_BASE}/music/${trackId}/stream`;
    a.play().then(() => setPreviewingId(trackId)).catch(() => {
      toast.error("Vorschau konnte nicht gestartet werden");
    });
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnd = () => setPreviewingId(null);
    a.addEventListener("ended", onEnd);
    return () => a.removeEventListener("ended", onEnd);
  }, []);

  const currentTrack = tracks.find((t) => t.id === music.track_id) || null;

  return (
    <section
      className="rounded-2xl border border-[#2a2a30] bg-[#0d0d0e]/70 p-4 sm:p-6 space-y-4"
      data-testid="editor-music-panel"
    >
      <audio ref={audioRef} preload="none" />
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#1f1f23] text-sky-300">
            <Music2 size={16} />
          </span>
          <div>
            <h3 className="text-base font-semibold text-white">Hintergrundmusik</h3>
            <p className="text-xs text-[#9a9aa3]">
              Wird über die gesamte Timeline gemischt. Ein Track pro Projekt.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowAutoMatch(true)}
            disabled={tracks.length === 0}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-medium text-white bg-[#1f1f23] hover:bg-[#2a2a30] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="editor-music-auto-match"
          >
            <Wand2 size={13} />
            Musik automatisch zuordnen
          </button>
          <button
            type="button"
            onClick={() => setShowSuno(true)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-medium text-white bg-[#1f1f23] hover:bg-[#2a2a30] transition-colors"
            data-testid="editor-music-suno"
          >
            <Sparkles size={13} />
            Suno-Prompts vorschlagen
          </button>
        </div>
      </header>

      {loadingTracks ? (
        <div className="flex items-center gap-2 text-sm text-[#9a9aa3]">
          <Loader2 size={14} className="animate-spin" /> Lade Bibliothek…
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <label className="block text-xs uppercase tracking-wider text-[#6a6a73] font-medium">
              Track aus Bibliothek
            </label>
            <div className="flex items-center gap-2">
              <select
                value={music.track_id || ""}
                onChange={(e) =>
                  onPatchMusic({ track_id: e.target.value || null })
                }
                className="flex-1 h-10 px-3 rounded-md bg-[#0a0a0b] border border-[#2a2a30] text-white text-sm focus:outline-none focus:border-[#ff6b35]"
                data-testid="editor-music-select"
              >
                <option value="">Keine Musik</option>
                {tracks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                    {t.artist ? ` · ${t.artist}` : ""}
                  </option>
                ))}
              </select>
              {currentTrack && (
                <button
                  type="button"
                  onClick={() => togglePreview(currentTrack.id)}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-[#1f1f23] hover:bg-[#2a2a30] text-white"
                  aria-label="Vorschau abspielen"
                  data-testid="editor-music-preview"
                >
                  {previewingId === currentTrack.id ? (
                    <Pause size={15} />
                  ) : (
                    <Play size={15} />
                  )}
                </button>
              )}
            </div>
            {tracks.length === 0 && (
              <p className="text-xs text-[#6a6a73]">
                Bibliothek ist leer. Lade unter &bdquo;Musik&ldquo; oben in der Navigation
                deine Tracks hoch.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center justify-between text-xs uppercase tracking-wider text-[#6a6a73] font-medium mb-1.5">
                <span>Lautstärke</span>
                <span className="text-white normal-case tracking-normal tabular-nums">
                  {music.volume_pct}%
                </span>
              </label>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={music.volume_pct}
                onChange={(e) =>
                  onPatchMusic({ volume_pct: parseInt(e.target.value, 10) })
                }
                className="w-full accent-[#ff6b35]"
                data-testid="editor-music-volume"
              />
            </div>
            <div className="flex items-center justify-between gap-3 sm:justify-start">
              <label className="text-sm text-[#d4d4d8] flex-1 leading-tight">
                Original-Ton automatisch leiser machen wenn jemand spricht
              </label>
              <button
                type="button"
                onClick={() => onPatchMusic({ auto_duck: !music.auto_duck })}
                className={`inline-flex w-12 h-7 rounded-full border transition-colors ${
                  music.auto_duck
                    ? "bg-[#ff6b35] border-[#ff6b35]"
                    : "bg-[#1f1f23] border-[#2a2a30]"
                }`}
                aria-pressed={music.auto_duck}
                data-testid="editor-music-ducking"
              >
                <span
                  className={`inline-block w-5 h-5 m-0.5 rounded-full bg-white transition-transform ${
                    music.auto_duck ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FadeSlider
              label="Sanft einblenden"
              value={music.fade_in_sec}
              onChange={(v) => onPatchMusic({ fade_in_sec: v })}
              testid="editor-music-fade-in"
            />
            <FadeSlider
              label="Sanft ausblenden"
              value={music.fade_out_sec}
              onChange={(v) => onPatchMusic({ fade_out_sec: v })}
              testid="editor-music-fade-out"
            />
          </div>
        </>
      )}

      {showAutoMatch && (
        <AutoMatchModal
          projectId={projectId}
          tracks={tracks}
          onClose={() => setShowAutoMatch(false)}
          onApply={async (suggestions) => {
            await onPatchClipsMusic(suggestions);
            setShowAutoMatch(false);
            toast.success("Vorschläge übernommen");
          }}
        />
      )}
      {showSuno && (
        <SunoPromptsModal
          projectId={projectId}
          onClose={() => setShowSuno(false)}
        />
      )}
    </section>
  );
}

function FadeSlider({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="flex items-center justify-between text-xs uppercase tracking-wider text-[#6a6a73] font-medium mb-1.5">
        <span>{label}</span>
        <span className="text-white normal-case tracking-normal tabular-nums">
          {Number(value).toFixed(1)} s
        </span>
      </label>
      <input
        type="range"
        min={0}
        max={5}
        step={0.1}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-sky-400"
        data-testid={testid}
      />
    </div>
  );
}

function AutoMatchModal({ projectId, tracks, onClose, onApply }) {
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(
          `/projects/${projectId}/auto-match-music`,
        );
        setSuggestions(data.suggestions || []);
      } catch (e) {
        toast.error("Auto-Match fehlgeschlagen: " + (e.message || "Netzwerk"));
        onClose();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const trackById = (id) => tracks.find((t) => t.id === id) || null;
  const swap = (idx, newTrackId) =>
    setSuggestions((s) =>
      s.map((item, i) =>
        i === idx
          ? {
              ...item,
              track_id: newTrackId || null,
              track_title: trackById(newTrackId)?.title || null,
              score: newTrackId ? Math.max(1, item.score) : 0,
            }
          : item,
      ),
    );

  return (
    <ModalShell title="Musik-Vorschläge" onClose={onClose} wide>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#9a9aa3] py-6">
          <Loader2 size={14} className="animate-spin" />
          Vergleiche Stimmungs-Tags…
        </div>
      ) : (
        <>
          <p className="text-xs text-[#9a9aa3] mb-4 leading-relaxed">
            Pro Clip ein Vorschlag basierend auf deinen Stimmungs-Tags. Du
            kannst Tracks tauschen oder Clips überspringen.
          </p>
          <ul className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {suggestions.map((s, i) => (
              <li
                key={s.clip_id}
                className="rounded-lg border border-[#2a2a30] bg-[#0d0d0e] px-3 py-2.5"
                data-testid={`automatch-row-${i}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-[#6a6a73] font-medium mb-1">
                  Clip {s.clip_index + 1}
                  {s.matched_tags.length > 0 && (
                    <span className="ml-1 normal-case tracking-normal text-sky-300">
                      · {s.matched_tags.join(", ")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={s.track_id || ""}
                    onChange={(e) => swap(i, e.target.value || null)}
                    className="flex-1 h-9 px-2 rounded-md bg-[#0a0a0b] border border-[#2a2a30] text-white text-sm focus:outline-none focus:border-[#ff6b35]"
                    data-testid={`automatch-select-${i}`}
                  >
                    <option value="">— Kein Match, manuell wählen —</option>
                    {tracks.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                        {t.artist ? ` · ${t.artist}` : ""}
                      </option>
                    ))}
                  </select>
                  {s.score > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-medium text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded">
                      <Check size={10} />
                      Match
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 rounded-md text-sm text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23]"
              data-testid="automatch-cancel"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => onApply(suggestions)}
              className="h-10 px-4 rounded-md text-sm font-semibold bg-[#ff6b35] hover:bg-[#ff8d65] text-white"
              data-testid="automatch-apply"
            >
              Alle übernehmen
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function SunoPromptsModal({ projectId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [prompts, setPrompts] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/projects/${projectId}/suno-prompts`);
        setPrompts(data.prompts || []);
      } catch (e) {
        toast.error("Konnte keine Prompts erzeugen: " + (e.message || "Netzwerk"));
        onClose();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copy = async (p) => {
    try {
      await navigator.clipboard.writeText(p.prompt);
      setCopiedId(p.id);
      toast.success("Prompt kopiert");
      setTimeout(() => setCopiedId((c) => (c === p.id ? null : c)), 1500);
    } catch {
      toast.error("Kopieren nicht möglich");
    }
  };

  return (
    <ModalShell title="Drei Musik-Vorschläge" onClose={onClose} wide>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#9a9aa3] py-6">
          <Loader2 size={14} className="animate-spin" /> Generiere Vorschläge…
        </div>
      ) : (
        <>
          <p className="text-xs text-[#9a9aa3] mb-4 leading-relaxed">
            Kopier einen Prompt, geh auf{" "}
            <a
              href="https://suno.com"
              target="_blank"
              rel="noreferrer"
              className="text-sky-300 hover:underline"
            >
              suno.com
            </a>
            , generiere deinen Track und lade die fertige MP3 hier wieder in
            deine Musik-Bibliothek hoch.
          </p>
          <ul className="space-y-3">
            {prompts.map((p) => (
              <li
                key={p.id}
                className="rounded-lg border border-[#2a2a30] bg-[#0d0d0e] px-4 py-3"
                data-testid={`suno-prompt-${p.id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="font-semibold text-white">{p.title}</div>
                  <button
                    type="button"
                    onClick={() => copy(p)}
                    className="inline-flex items-center gap-1.5 text-xs text-[#9a9aa3] hover:text-white border border-[#2a2a30] hover:border-[#3a3a40] rounded-md px-2.5 py-1.5 transition-colors"
                    data-testid={`suno-copy-${p.id}`}
                  >
                    {copiedId === p.id ? <Check size={12} /> : <Copy size={12} />}
                    {copiedId === p.id ? "Kopiert" : "Kopieren"}
                  </button>
                </div>
                <pre className="text-[13px] text-[#d4d4d8] whitespace-pre-wrap leading-relaxed font-sans">
                  {p.prompt}
                </pre>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex items-center justify-end gap-2">
            <a
              href="https://suno.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm font-semibold bg-gradient-to-r from-[#ff6b35] to-[#ff8d65] text-white"
              data-testid="suno-open-link"
            >
              Suno öffnen
              <ExternalLink size={13} />
            </a>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function ModalShell({ title, children, onClose, wide }) {
  return (
    <div
      className="fixed inset-0 z-[50] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className={`w-full ${wide ? "max-w-2xl" : "max-w-md"} rounded-2xl border border-[#2a2a30] bg-[#0a0a0b] shadow-[0_20px_60px_rgba(0,0,0,0.6)] max-h-[90vh] flex flex-col`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a2a30]">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23]"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// Tag input used by EditorEdit per-clip mood_tags row.
export function ClipMoodTagsInput({ clip, onChange, testid }) {
  const [value, setValue] = useState((clip.mood_tags || []).join(", "));
  useEffect(() => {
    setValue((clip.mood_tags || []).join(", "));
  }, [clip.mood_tags]);
  return (
    <label className="block">
      <span className="sr-only">Stimmungs-Tags für diesen Clip</span>
      <div className="flex items-center gap-1.5">
        <Tag size={11} className="text-[#6a6a73] shrink-0" />
        <input
          type="text"
          placeholder="z. B. strand, chill"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => {
            const tags = value
              .split(",")
              .map((s) => s.trim().toLowerCase())
              .filter(Boolean);
            const before = (clip.mood_tags || []).join(",");
            const after = tags.join(",");
            if (before !== after) onChange(tags);
          }}
          className="flex-1 h-7 px-2 rounded bg-[#0a0a0b] border border-[#2a2a30] text-white text-[11px] focus:outline-none focus:border-sky-400"
          data-testid={testid}
        />
      </div>
    </label>
  );
}
